import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "brema-recent-destinations";
const MAX_RECENT = 5;

/**
 * Persist the user's last few chosen destinations in localStorage so they
 * can be re-picked with a single tap on subsequent sessions.
 * Each entry: { name: string, lat: number, lng: number, ts: number }.
 * The list is de-duplicated by (lat, lng) rounded to 4 decimals (~11 m).
 */
export function useRecentDestinations() {
  const [recents, setRecents] = useState([]);

  // Load on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setRecents(parsed.slice(0, MAX_RECENT));
      }
    } catch { /* ignore corrupt storage */ }
  }, []);

  const push = useCallback((entry) => {
    if (!entry || typeof entry.lat !== "number" || typeof entry.lng !== "number") return;
    const dedupeKey = (r) => `${r.lat.toFixed(4)},${r.lng.toFixed(4)}`;
    const key = dedupeKey(entry);
    setRecents((prev) => {
      const filtered = prev.filter((r) => dedupeKey(r) !== key);
      const next = [{ ...entry, ts: Date.now() }, ...filtered].slice(0, MAX_RECENT);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch { /* quota exceeded — silently drop */ }
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setRecents([]);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }, []);

  return { recents, push, clear };
}
